import { isServer } from './env-helper';

type UserId = string;
type UserIdForTrack = string; // '0'으로 앞자리 패딩된 최소 5글자짜리 user id
type Currency = string;
type Network = string;
type HashString = string;
type Version = string;

// 레거시 케이스(`flipster.closedEmergencies.v2`, `flipster.tv`) 제외하고
// `flipster.configs.버전.` 형태로 사용하기로 함
export interface LocalStorage {
  [key: `flipster.configs.v1.${UserId}.skipInstantFlipModal`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipCloseAllPositionsConfirm`]: boolean;
  [key: `flipster.configs.v1.${UserId}.${Currency}-${Network}.skipMemoRequiredConfirm`]: boolean;
  [key: `flipster.configs.v1.${UserId}.${HashString}.skipCurrencyWarningConfirm`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipPositionFullCloseModal`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipAirdropPositionCloseModal`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipSingleTpslOnlyPopup`]: boolean;
  [
    key: `flipster.configs.v1.${UserId}.skipTradeOnboarding`
  ]: boolean /** trade 페이지에서 onboading을 확인했는지 여부 */;
  [key: `flipster.configs.v1.${UserId}.recentSearchHistory`]: string[];
  [key: `flipster.configs.v1.${UserId}.pnlAnalysisRealizedOnlyMode`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipRewardHubConfirm`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipHedgingNoticeConfirm`]: boolean;
  [key: `flipster.configs.v1.${UserId}.showAllPerp`]: boolean;
  [key: `flipster.configs.v1.${UserId}.showAllSpot`]: boolean;
  [key: `flipster.configs.v1.${UserId}.assetsOverviewExpanded`]: boolean;
  [key: `flipster.configs.v1.${UserId}.assetsPerpetualTabOverviewExpanded`]: boolean;
  [key: `flipster.configs.v1.${UserId}.assetsSpotTabOverviewExpanded`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipFlipWarningInProfitBoost`]: boolean;
  [key: `flipster.configs.v1.${UserId}.skipAdditionalOrderWarningInProfitBoost`]: boolean;
  [key: `flipster.configs.v1.${UserId}.pipMode`]: boolean;
  [key: `flipster.configs.v1.${UserId}.tncAgreementModalHideTimestamp`]: string;
  [key: `flipster.configs.v1.${UserId}.pnlMode`]: 'unrealizedPnl' | 'netPnl';
  [key: `flipster.configs.v1.amplitude.${UserIdForTrack}.variants`]: unknown; // amplitude Variants | undefined
  [key: `flipster.configs.v1.accordion.${UserId}.${string}`]: boolean;
  'flipster.configs.v1.nutsAndUserId': string;
  'flipster.configs.v1.closedAssetsPageBanner': string | number[];
  'flipster.configs.v1.closedLaunchpoolPageBanner': string[];
  'flipster.closedEmergencies.v2': number[];
  'flipster.configs.v1.leverage': Record<string, number>;
  'flipster.configs.v1.orderTypeSelect': 'margin' | 'positionSize';
  'flipster.configs.v1.priceTypeTab': 'market' | 'trigger';
  'flipster.configs.v1.kycModalHideTimestamp': string;
  'flipster.configs.v1.closeFomoBanner': number; // close한 datetime
  'flipster.configs.v1.spot.sideTypeTab': 'Buy' | 'Sell';
  'flipster.configs.v1.leaderboardLeagueOnboardingModal': boolean;
  'flipster.configs.v1.editProfileOnboarding': boolean;
  'flipster.configs.v1.profileNotificationsOnboarding': boolean;
  'flipster.configs.v1.profile.feedTabRedDot': boolean;
  'flipster.configs.v1.accountRedDot': boolean;
  'flipster.configs.v1.skipTriggerTimeTooltip': boolean;
  'flipster.configs.v1.shareThisTradeChecked': boolean;
  // Share this trade in the feed 모달을 띄우는 조건을 계산
  // 1. share 하지 않은 유저에게 1회 노출
  // 2. 모달을 본 후 1주일 내에도 share하지 않으면 1회 추가 노출
  // UNSEEN -> 모달이 아직 한번도 노출되지 않음
  // READY -> timestamp 시간에 1회 노출이 이루어졌음
  // COMPLETE -> 모달이 2번 노출된 상태
  'flipster.configs.v1.shareThisTradeNudgeState': ['UNSEEN'] | ['READY', number /* timestamp */] | ['COMPLETE'];
  'flipster.configs.v1.feedTabRedDot.livePnlShoutout': boolean;
  'flipster.configs.v1.marketPulseOpen': boolean;
  'flipster.configs.v1.amplitude.variants': unknown; // amplitude Variants | undefined

  'flipster.configs.v1.trade_settings_landed_features': string[];
  'flipster.configs.v1.displayed_leverage_type': 'positionLeverage' | 'effectiveLeverage';
  'flipster.configs.v1.order_tutorial_v2_feature_tooltip': boolean;

  'flipster.configs.v1.pip_button': boolean;
  'flipster.configs.v1.notice_upnl_earn': boolean;

  'flipster.tv.whaleTracker.red-dot': boolean;

  'flipster.configs.v1.tncAgreementModalHideTimestamp': string;
  'flipster.configs.v1.lastViewedNotificationTime': string | null;

  // profile - oauth related key
  [key: `flipster.configs.v1.${Version}.editProfileDraftForOAuth`]: unknown; // edit profile payload
  'flipster.configs.v1.x-oauth.state': string;
  'flipster.configs.v1.x-oauth.verifier': string;

  'flipster.configs.v1.prexInstallationId': string; // amplitude 에서 사용하는 prex_installation_id
}

export const getLocalStorage = <T extends keyof LocalStorage>(key: T) => {
  if (isServer) return null;
  const value = localStorage?.getItem(key);
  try {
    if (!value) return null;
    return JSON.parse(value) as LocalStorage[T];
  } catch {
    return (value as LocalStorage[T]) ?? null;
  }
};

export const setLocalStorage = <T extends keyof LocalStorage>(key: T, value: LocalStorage[T]) => {
  if (isServer) return;
  localStorage?.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
};

export const deleteLocalStorage = <T extends keyof LocalStorage>(key: T) => {
  if (isServer) return;
  localStorage?.removeItem(key);
};

export const getFlipsterConfigsV1Key = <
  T extends
    | 'skipInstantFlipModal'
    | 'skipCloseAllPositionsConfirm'
    | 'skipPositionFullCloseModal'
    | 'skipTradeOnboarding'
    | 'recentSearchHistory'
    | 'skipRewardHubConfirm'
    | 'skipHedgingNoticeConfirm'
    | 'showAllPerp'
    | 'showAllSpot'
    | 'assetsOverviewExpanded'
    | 'assetsPerpetualTabOverviewExpanded'
    | 'assetsSpotTabOverviewExpanded'
    | 'skipFlipWarningInProfitBoost'
    | 'skipAdditionalOrderWarningInProfitBoost'
    | 'pipMode'
    | 'skipAirdropPositionCloseModal'
    | 'pnlAnalysisRealizedOnlyMode'
    | 'tncAgreementModalHideTimestamp'
    | 'pnlMode',
>(
  userId: number,
  type: T,
) => `flipster.configs.v1.${userId}.${type}` as const;
